import {
  FGGridItem,
  FGButton,
  FGCheckbox,
  FGSelect,
  FGSelectOption,
  FGInputField,
  FGInput,
  FGTypography,
  FGGridContainer,
  FGInlineAlert,
  FGIcons,
  IconsLarge,
  FGPassword,
} from '@fg/design-system';
import { useEffect, useState } from 'react';
import { userRegistration, verifyEmail, userVerifyCaptcha } from '../services/Login';
import clsx from 'clsx';
import he from 'he';
import { auto } from '@popperjs/core';
import Turnstile from "react-turnstile";
import { HeaderFooter } from './HeaderFooter';

type Props = {
  customProps: {
    logoType: string;
    appCode: string;
    redirect: string;
    csrfToken: string;
    sruMessage: string;
    countries: string[];
    states: { [key: string]: string[] };
    clientTypes: string[];
    jobRoles: { [key: string]: string[] };
    stateOauth: string;
    callback: string;
    captchaKey: string;
  };
};

export const UserRegistration = ({ customProps }: Props) => {
  const { logoType, appCode, csrfToken, sruMessage, countries, states, clientTypes, jobRoles, stateOauth, callback, captchaKey } = customProps;
  const [userEmail, setUserEmail] = useState('');
  const [allCountries, setAllCountries] = useState([]);
  const [allStates, setAllStates] = useState({});
  const [allClientTypes, setAllClientTypes] = useState([]);
  const [allJobRoles, setAllJobRoles] = useState({});
  const [stateOptions, setStateOptions] = useState([]);
  const [stateDropdownKey, setStateDropdownKey] = useState(0);
  const [previousCountry, setPreviousCountry] = useState('');
  const [statesAvailable, setStatesAvailable] = useState(false);
  const [currentClientType, setCurrentClientType] = useState('');
  const [jobRoleOptions, setJobRoleOptions] = useState([]);
  const [jobRolesAvailable, setJobRolesAvailable] = useState(false);
  const [jobRoleDropdownKey, setJobRoleDropdownKey] = useState(0);
  const [newPassword, setNewPassword] = useState();
  const [confirmPassword, setConfirmPassword] = useState();
  const [passwordMatched, setPasswordMatched] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [sruBannerMessage, setSruBannerMessage] = useState('');
  const [isTermsOfUse, setIsTermsOfUse] = useState(false);
  const [isEventsPolicy, setisEventsPolicy] = useState(false);
  const [isCookiePolicy, setisCookiePolicy] = useState(false);
  const [isStateRequired, setIsStateRequired] = useState(false);
  const [areAllRequiredFieldsFilled, setAreAllRequiredFieldsFilled] = useState(false);
  const [status, setStatus] = useState('');
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    jobRole: '',
    country: '',
    state: '',
    password: '',
    whatBestDescribesYou: '',
    appCode: appCode,
    stateOauth: stateOauth,
    callback: callback
  });
  const newPasswordConstant = 'new-password';
  const linkStyle = { textDecoration: 'none' };
  const optionalCountries = ["Switzerland", "China", "Germany", "Ireland", "Italy"];
  const copyrightText = "Copyright © 2025 Fitch Ratings, Inc., Fitch Solutions, Inc., and their subsidiaries.";

  useEffect(() => {
    const decodedCountries = JSON.parse(he.decode(countries));
    const decodedStates = JSON.parse(he.decode(states));
    const decodedClientTypes = JSON.parse(he.decode(clientTypes));
    const decodedJobRoles = JSON.parse(he.decode(jobRoles));

    setAllCountries(decodedCountries);
    setAllStates(decodedStates);
    setAllClientTypes(decodedClientTypes);
    setAllJobRoles(decodedJobRoles);
  }, []);

  const countryOptions: FGSelectOption[] = allCountries.map((country) => ({
    value: country,
    label: country,
  }));

  const clientTypeOptions: FGSelectOption[] = allClientTypes.map((type) => ({
    value: type,
    label: type
  }));

  const loginCaptchaCall = (payload) => {
    setErrorMessage('');
    return userVerifyCaptcha(payload)
      .then((response) => {
        if (response.status !== 'success') {
          setStatus(response.status);
          setErrorMessage(response.message);
          return false;
        } else {
          setStatus(response.status);
          return true;
        }
      })
      .catch((error: any) => {
        setErrorMessage(error);
      })
  };

  const PasswordTooltip: React.FC<{ value: string }> = ({ value }) => {
    const has8Characters = value.length >= 8;
    const has1Number = !!value.match(/[0-9]/g)?.length;
    const upperCaseLetter = /[A-Z]/.test(value);
    const lowerCaseLetter = /[a-z]/.test(value);

    const Item: React.FC<{ children: any; isValid: boolean }> = ({ children, isValid }) => (
      <li
        className="fg-d--f fg-ai--c fg-mg--b1"
        style={{
          color: isValid ? 'var(--fg-green-green-700)' : 'var(--fg-grey-grey-600)',
        }}
      >
        <FGIcons large={IconsLarge.Check2} className="fg-mg--r2" />
        {children}
      </li>
    );

    return (
      <div style={{ width: '243px' }}>
        <h3 className="fg-body4 fg-mg--b1" style={{ color: 'var(--fg-primary-black)' }}>
          New password must contain
        </h3>
        <ol className="fg-body4" style={{ listStyle: 'none' }}>
          <Item isValid={has8Characters}>At least 8 characters</Item>
          <Item isValid={has1Number}>At least 1 number</Item>
          <Item isValid={upperCaseLetter}>At least one upper case character</Item>
          <Item isValid={lowerCaseLetter}>At least one lower case character</Item>
        </ol>
      </div>
    );
  };
  const [newPasswordTooltipContent, setNewPasswordTooltipContent] = useState(<PasswordTooltip value="" />);
  const [confirmPasswordTooltipContent, setConfirmPasswordTooltipContent] = useState(<PasswordTooltip value="" />);

  const isPasswordValid = (value) => {
    const has8Characters = value.length >= 8;
    const has1Number = !!value.match(/[0-9]/g)?.length;
    const upperCaseLetter = /[A-Z]/.test(value);
    const lowerCaseLetter = /[a-z]/.test(value);
    return has8Characters && has1Number && upperCaseLetter && lowerCaseLetter;
  };

  const tooltipContentLogic = (e: React.ChangeEvent<HTMLInputElement>, input: 'new-password' | 'confirm-password') => {
    const component = <PasswordTooltip value={e.target.value} />;
    input === newPasswordConstant ? setNewPasswordTooltipContent(component) : setConfirmPasswordTooltipContent(component);
    handleChange(e, input);
  };

  useEffect(() => {
    setSruBannerMessage(sruMessage);
  }, [sruMessage]);

  useEffect(() => {
    if (newPassword && confirmPassword && newPassword === confirmPassword && isPasswordValid(newPassword)) {
      setPasswordMatched(true);
    } else {
      setPasswordMatched(false);
    }
  }, [newPassword, confirmPassword]);

  useEffect(() => {
    const storedUserEmail = sessionStorage.getItem('userEmail');
    setUserEmail(storedUserEmail);
    setUser({ ...user, email: storedUserEmail });
  }, []);

  const handleChange = (e, input) => {
    if (input === newPasswordConstant) {
      setNewPassword(e.target.value);
      setUser({ ...user, password: e.target.value })
    } else {
      setConfirmPassword(e.target.value);
    }
  };

  const handleCountryChange = (selectedOption) => {

    const selectedCountry = selectedOption ? selectedOption.value : '';  // Extract the value from the selected option or set to empty string if null

    // Update the country field in the user state
    setUser(prevUser => ({
      ...prevUser,
      country: selectedCountry,
      // reset the state every time we change the country value
      state: ''
    }));

    // Update the state options based on the selected country
    const countryKey = selectedCountry.replace(/ /g, '_');
    const statesForCountry = allStates[countryKey] || [];

    // Map the states to an array of objects with value and label properties
    const stateOptionObjects: FGSelectOption[] = statesForCountry.map(stateString => {
      const [value, label] = stateString.split(':');
      return { value, label };
    });

    // Check if the country allows a state value to be optional
    !optionalCountries.includes(selectedCountry) && stateOptionObjects.length > 0 ? setIsStateRequired(true) : setIsStateRequired(false);

    setStateOptions(stateOptionObjects);
    setStatesAvailable(stateOptionObjects.length > 0);
    setStateDropdownKey(prevKey => prevKey + 1);
  };

  // Function to handle when the state is selected
  const handleStateChange = (selectedOption) => {
    const selectedState = selectedOption ? selectedOption.value : '';

    setUser({
      ...user,
      state: selectedState
    });
  };

  const handleClientTypeChange = (selectedOption) => {
    const selectedType = selectedOption ? selectedOption.value : '';
    setCurrentClientType(selectedType);

    const jobRolesForType = allJobRoles[selectedType] || [];

    const jobRoleObjects: FGSelectOption[] = jobRolesForType.map((roleString) => ({
      value: roleString,
      label: roleString
    }))

    setUser({
      ...user,
      jobRole: '',
      whatBestDescribesYou: selectedType
    })

    setJobRoleOptions(jobRoleObjects);
    setJobRolesAvailable(jobRoleObjects.length > 0);
    setJobRoleDropdownKey(prevKey => prevKey + 1);
  }

  const handleJobRoleChange = (selectedOption) => {
    const selectedRole = selectedOption ? selectedOption.value : '';

    setUser({
      ...user,
      jobRole: selectedRole
    })
  }

  const handeTermsOfUseChange = () => {
    setIsTermsOfUse(!isTermsOfUse);
  };
  const handleEventsPolicyChange = () => {
    setisEventsPolicy(!isEventsPolicy);
  };
  const handleCookiePolicyChange = () => {
    setisCookiePolicy(!isCookiePolicy);
  };

  const handleClickButton = () => {
    userRegistration(user, csrfToken)
      .then((response) => {
        if (response.redirectUrl) {
          console.log('Redirecting to: ' + response.redirectUrl + "...");
          window.location.href = response.redirectUrl;
        }
        if (response.error || response.message) {
          setErrorMessage(response.message);
        }
      })
      .catch((error: any) => {
        setErrorMessage(error);
      });
  };

  const handleClickResendLinkButton = () => {
    verifyEmail(user, csrfToken)
      .then((response) => {
        if (response.redirectUrl) {
          console.log('Redirecting to: ' + response.redirectUrl + "...");

        }
        if (response.error || response.message) {
          setErrorMessage(response.message);
        }
      })
      .catch((error: any) => {
        setErrorMessage(error);
      });
  };

  useEffect(() => {
    const areAllFieldsFilled = checkAllFieldsFilled();
    setAreAllRequiredFieldsFilled(areAllFieldsFilled && passwordMatched);

    if (user.country !== previousCountry) {
      setPreviousCountry(user.country);
    }
  }, [user, previousCountry, statesAvailable, passwordMatched]);

  const checkAllFieldsFilled = () => {
    return Object.entries(user).every(([key, value]) => {
      if (key === 'callback' || key === 'stateOauth') return true;
      if (key === 'state') {
        return !statesAvailable || !isStateRequired || (typeof value === 'string' && value.trim() !== '');
      }
      return typeof value === 'string' && value.trim() !== '';
    });
  };

  const displayUserErrors = () => {
    if (!areAllRequiredFieldsFilled && isTermsOfUse && isEventsPolicy && isCookiePolicy) {
      return 'Please fill in all required fields.';
    }

    return null;
  };

  function FormLabel({ id, text, isRequired }) {
    return (
      <FGInputField id={id}>
        <div style={{ paddingBottom: '8px' }}>
          <span className="fg-input-field__label">{text}</span>
          {isRequired && (
            <span className="fg-input-field__label" style={{ color: 'red' }}> * </span>
          )}
        </div>
      </FGInputField>
    )
  }

  function TermsOfUse() {
    return (
      <label className="fg-checkbox__label">
        I agree to the
        {' '}
        <a href="https://www.fitch.group/terms-of-use" target="_blank" rel="noopener noreferrer" style={linkStyle}>Terms Of Use</a>
        .
      </label>
    );
  }
  function EventsPolicy() {
    return (
      <label className="fg-checkbox__label">
        I agree to the terms and conditions in the
        {' '}
        <a href="https://www.fitch.group/event-policy" target="_blank" rel="noopener noreferrer" style={linkStyle}>Events Policy</a>
        .
      </label>
    );
  }
  function CookiePolicy() {
    return (
      <label className="fg-checkbox__label">
        I consent to the use of cookies by creating my account as described in the
        {' '}
        <a href="https://www.fitch.group/cookie-policy" target="_blank" rel="noopener noreferrer" style={linkStyle}>Cookie Policy</a>
        .
      </label>
    );
  }

  const userErrors = displayUserErrors();

  return (
    <HeaderFooter appCode={appCode} logoType={logoType} copyrightText={copyrightText}>
      <div>
        {!sruBannerMessage && (<div className="user-registration" data-testid="user-registration-component">
          {errorMessage && (<div className={clsx('fg-ta--c', 'fg-error-banner-story__banner-wrapper')}>
            <FGInlineAlert type="error">{errorMessage}</FGInlineAlert>
          </div>
          )}
          {userErrors && (<div className={clsx('fg-ta--c', 'fg-error-banner-story__banner-wrapper')}>
            <FGInlineAlert type="error">{userErrors}</FGInlineAlert>
          </div>
          )}
          {sruBannerMessage && (<div className={clsx('fg-ta--c', 'fg-error-banner-story__banner-wrapper')}>
            <FGInlineAlert type="success">{sruBannerMessage}</FGInlineAlert>
          </div>
          )}
          <div className="fg-mg--t10 fg-fxd--c tab-panel-content" style={{ justifyContent: 'center', maxWidth: '611px', paddingTop: '24px' }}>
            <FGTypography tag="h3" style="headline3" alignment="center">
              Create your free account
            </FGTypography>
            <FGGridContainer
              xs={2}
              md={2}
              columns={2}
              isFluid
              style={{ paddingTop: '64px', gridGap: '24px' }}
            >
              <FGGridItem>
                <FormLabel id={1} text={"First Name"} isRequired={true} />
                <FGInput
                  size="l"
                  id="2"
                  autoFocus
                  autoComplete="firstName"
                  name="firstName"
                  onChange={(e) => setUser({ ...user, firstName: e.target.value.trim() })}
                  hasError={!user.firstName && userErrors !== null}
                  style={{ paddingBottom: '8px' }}
                />
              </FGGridItem>
              <FGGridItem>
                <FormLabel id={3} text={"Last Name"} isRequired={true} />
                <FGInput
                  size="l"
                  id="4"
                  autoComplete="lastName"
                  name="lastName"
                  onChange={(e) => setUser({ ...user, lastName: e.target.value.trim() })}
                  hasError={!user.lastName && userErrors !== null}
                  style={{ paddingBottom: '8px' }}
                />
              </FGGridItem>
              <FGGridItem>
                <FormLabel id={5} text={"Location"} isRequired={true} />
                <FGSelect
                  size="large"
                  onChange={handleCountryChange}
                  options={countryOptions}
                  placeholder='Select...'
                  hasError={!user.country && userErrors !== null}
                  isClearable={false}
                  listMaxHeight={200}
                  isPortalDisabled
                />
              </FGGridItem>
              <FGGridItem>
                <FormLabel id={7} text={"State"} isRequired={isStateRequired} />
                <FGSelect
                  key={stateDropdownKey}
                  size="large"
                  onChange={handleStateChange}
                  options={stateOptions}
                  placeholder='Select...'
                  isDisabled={!statesAvailable}
                  hasError={statesAvailable && isStateRequired && !user.state && userErrors !== null}
                  isClearable={!isStateRequired}
                  listMaxHeight={200}
                  isPortalDisabled
                />
              </FGGridItem>
            </FGGridContainer>
            <br></br>
            <FGGridContainer
              xs={1}
              md={1}
              columns={1}
              isFluid
              style={{ paddingTop: '16px', gridGap: '32px' }}
            >
              <FGGridItem>
                <FormLabel id={9} text={"Company Name"} isRequired={true} />
                <FGInput
                  size="l"
                  id="14"
                  autoComplete="companyName"
                  name="companyName"
                  onChange={(e) => setUser({ ...user, companyName: e.target.value.trim() })}
                  hasError={!user.companyName && userErrors !== null}
                />
              </FGGridItem>
            </FGGridContainer>
            <br></br>
            <FGGridContainer
              xs={2}
              md={2}
              columns={2}
              isFluid
              style={{ paddingTop: '16px', gridGap: '24px' }}
            >
              <FGGridItem>
                <FormLabel id={11} text={"What best describes you"} isRequired={true} />
                <FGSelect
                  size="large"
                  onChange={handleClientTypeChange}
                  options={clientTypeOptions}
                  placeholder='Select...'
                  hasError={!currentClientType && userErrors !== null}
                  isClearable={false}
                  listMaxHeight={200}
                  isSearchable={false}
                  isPortalDisabled
                />
              </FGGridItem>
              <FGGridItem>
                <FormLabel id={13} text={"Job Role"} isRequired={true} />
                <FGSelect
                  key={jobRoleDropdownKey}
                  size="large"
                  onChange={handleJobRoleChange}
                  options={jobRoleOptions}
                  placeholder='Select...'
                  isDisabled={!jobRolesAvailable}
                  hasError={jobRolesAvailable && !user.jobRole && userErrors !== null}
                  isClearable={false}
                  listMaxHeight={200}
                  isPortalDisabled
                />
              </FGGridItem>
            </FGGridContainer>
            <br></br>
            <FGGridContainer
              xs={1}
              md={1}
              columns={1}
              isFluid
              style={{ paddingTop: '16px', gridGap: '32px' }}
            >
              <FGGridItem>
                <FormLabel id={15} text={"Create Password"} isRequired={true} />
                <FGPassword
                  size="l"
                  id="15"
                  tooltipContent={newPasswordTooltipContent}
                  autoComplete="off"
                  name="password"
                  onChange={(e: any) => tooltipContentLogic(e, newPasswordConstant)}
                  hasError={!user.password && userErrors !== null}
                />
              </FGGridItem>
              <FGGridItem>
                <FormLabel id={17} text={"Confirm Password"} isRequired={true} />
                <FGPassword
                  size="l"
                  id="17"
                  tooltipContent={confirmPasswordTooltipContent}
                  autoComplete="off"
                  name="confirmPassword"
                  onChange={(e: any) => tooltipContentLogic(e, 'confirm-password')}
                  rightElement={passwordMatched && <FGIcons large={IconsLarge.Check2} style={{ color: 'var(--fg-green-green-700' }} />}
                  hasError={!passwordMatched && userErrors !== null}
                />
              </FGGridItem>
            </FGGridContainer>
            <br></br>
            <div style={{ margin: auto, maxWidth: '570px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '24px' }}>
                <Turnstile
                  sitekey={captchaKey}
                  theme='light'
                  onVerify={(token) => loginCaptchaCall({ token: token })}
                  onError={() => {
                    setErrorMessage('An error occurred with Cloudflare, please try again later or refresh your page');
                    setStatus('error');
                  }}
                  onExpire={() => {
                    setErrorMessage('The Cloudflare token provided has expired, please try again');
                    setStatus('error');
                  }}
                />
              </div>
              <br></br>
              <br></br>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FGTypography color="strong" tag="p" style="body1" alignment="left" className="fg-checkbox__label" cssStyle={{ maxWidth: '570px', margin: 'auto' }} >
                  By creating an account on the Fitch Ratings website, you consent to the collection and use of your personal data for purposes including enhancing user experience, optimizing our services, and transferring your data outside of your jurisdiction. You may withdraw your consent at any time by deleting your account. If you want to learn more about data privacy and cookies on our website, please read the
                  {' '}
                  <a href="https://www.fitch.group/policies" target="_blank" rel="noopener noreferrer" style={linkStyle}>Privacy Policy</a>
                  {'.'}
                </FGTypography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', paddingTop: '30px', paddingBottom: '10px' }}>
                <FGCheckbox
                  id="terms-of-use"
                  onChange={handeTermsOfUseChange}
                />
                <TermsOfUse />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', paddingTop: '10px', paddingBottom: '10px' }}>
                <FGCheckbox
                  id="events-policy"
                  onChange={handleEventsPolicyChange}
                />
                <EventsPolicy />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', paddingTop: '10px', paddingBottom: '20px' }}>
                <FGCheckbox
                  id="cookie-policy"
                  onChange={handleCookiePolicyChange}
                />
                <CookiePolicy />
              </div>
              <div style={{ margin: auto, maxWidth: '370px', paddingTop: '12px', paddingBottom: '32px' }}>
                <FGButton
                  fullWidth
                  size="l"
                  disabled={!passwordMatched || !isTermsOfUse || !isEventsPolicy || !isCookiePolicy || !areAllRequiredFieldsFilled || status !== 'success'}
                  onClick={handleClickButton}
                >
                  Create Account
                </FGButton>
              </div>
            </div>
          </div>
        </div>
        )}
        {sruBannerMessage && (
          <div
            className="fg-mg--t10 fg-fxd--c"
            style={{
              display: 'flex',
              maxWidth: '418px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            {errorMessage && (<div className={clsx('fg-ta--c', 'fg-error-banner-story__banner-wrapper')}>
              <FGInlineAlert type="error">{errorMessage}</FGInlineAlert>
            </div>
            )}
            <div className="fg-fxd--c" style={{ display: 'flex' }}>
              <FGTypography tag="h6" style="headline6">
                <div
                  style={{
                    display: 'flex',
                  }}
                  className="fg-pd--b2 fg-ai--c fg-jc--c"
                >
                  <FGIcons 
                    icon="Check" 
                    className="fg-mg--r3"
                    size={32}
                    style={{
                      color: 'var(--fg-green-green-600)',
                      display: 'flex'
                    }}
                  />
                  <FGTypography tag="h1" style="headline3" color="strong" alignment="center">
                    Check your inbox
                  </FGTypography>
                </div>
              </FGTypography>
              <p
                className="fg-body3-long fg-ta--c fg-mg--0"
                style={{
                  textAlign: "left",
                  paddingTop: 64,
                  paddingBottom: 32,
                  color: 'var(--fg-grey-grey-700)'
                }}
              >
                {sruBannerMessage}
              </p>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '150px',
                background: 'linear-gradient(to bottom, #D3D3D3, #FBFBFB)',
                borderRadius: '4px',
                position: 'relative'
              }}>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    right: '-24px',
                    backgroundColor: 'grey',
                    color: 'white',
                    borderRadius: '50%',
                    padding: '2px 6px',
                    fontSize: '12px',
                    zIndex: 1
                  }}>
                    1
                  </span>
                  <FGIcons 
                    icon="Notifications" 
                    size={64} 
                    className="fg-pos--a" 
                    style={{
                      left: "50%",
                      transform: "translateX(-50%)",
                      fill: "#D3D3D3",
                      color: "gray"
                    }}
                  />
                </div>
              </div>
              <p
                className="fg-body3-long fg-ta--c fg-mg--0"
                style={{
                  textAlign: "left",
                  paddingTop: 32,
                  color: 'var(--fg-grey-grey-700)',
                }}
              >
                If you don't receive the email within 10 minutes, please check your spam folder or try again.
              </p>
            </div>

            <div style={{
              display: 'flex',
              marginLeft: 'auto',
              marginRight: 'auto',
              paddingTop: 32
            }}>
              <FGButton variant="secondary" size="l" iconPlacement="left" fullWidth
                onClick={handleClickResendLinkButton}>
                <div style={{ paddingLeft: 100, paddingRight: 100 }}>
                  <FGIcons large={IconsLarge.Refresh} />
                  <span style={{ paddingLeft: 10 }}>
                    Resend Link
                  </span>
                </div>
              </FGButton>
            </div>
          </div>
        )}
      </div>
    </HeaderFooter>
  );
};